import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { fragmentContainsNodeType } from '@atlaskit/editor-common/insert';
import { getParentOfTypeCount, getPositionAfterTopParentNodeOfType } from '@atlaskit/editor-common/nesting';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { hasParentNodeOfType, safeInsert } from '@atlaskit/editor-prosemirror/utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { findFirstReplaceStep, getValidEndTextPosition } from '../../../../pm-plugins/decoration/utils/step-utils';
/**
 * Figure out what to do with these, possibly export/shift into editor-common
 * during move into editor-core
 */
import { getSupportedNodesForAIModal } from '../../../utils/ai-button';
import { addAnalytics } from '../../../utils/analytics/utils';
/**
 * Unsure if we need this - add this 'hook' for now, started with just meta
 * but also folded in `setSelection` logic to reduce that complexity
 *
 * DO NOT pass transaction around beyond here
 */
export function beforeDispatchAITransaction(tr, editorState) {
  /**
   * DO NOT REMOVE isAiContentTransformation from transaction meta.
   * Proactive AI S+G rely on isAiContentTransformation meta to
   * 	ignore changes in document made by AI modal.
   * Why ignore it?
   * - Response returned from AI does not require Spelling and Grammar check.
   * - We have assumed that it's already correct from S+G perspective.
   */
  tr.setMeta('isAiContentTransformation', true);
  var firstReplaceStep = findFirstReplaceStep(tr.steps);
  if (!firstReplaceStep) {
    // Ideally we should always have replace step for for AI transaction.
    // But in case if there isn't we want to fire analytic event so we can monitor it.
    addAnalytics({
      editorState: editorState,
      tr: tr,
      payload: {
        action: 'failed',
        actionSubject: 'editorPluginAI',
        actionSubjectId: 'experienceApplication',
        attributes: {
          errorType: 'replaceStepNotFound'
        },
        eventType: EVENT_TYPE.OPERATIONAL
      }
    });
    return tr;
  }
  var validPositionToSet = getValidEndTextPosition({
    step: firstReplaceStep,
    doc: tr.doc
  });
  if (validPositionToSet !== 0) {
    tr.setSelection(TextSelection.create(tr.doc, validPositionToSet));
  }
  return tr;
}
export function beforeDispatchingTransactionForBlocks(fragment, tr, state) {
  var supportedNodeTypes = getSupportedNodesForAIModal(state);
  // Here we are checking if there is text node at first level.
  // Then we will highlight all nodes.
  var areTopLevelBlockElements = true;
  fragment.forEach(function (node) {
    areTopLevelBlockElements = areTopLevelBlockElements && supportedNodeTypes.has(node.type);
  });
  if (areTopLevelBlockElements) {
    tr.setMeta('isAiContentTransformationForBlocks', true);
  }
}
export function beforeDispatchAIRovoAgentTransaction(tr) {
  /**
   * DO NOT REMOVE
   * This is used when distinguishing content generated by a Rovo agent
   */
  tr.setMeta('isAiRovoAgentContentTransformation', true);
  return tr;
}
function getLastContentDepth(_ref) {
  var pmFragment = _ref.pmFragment;
  var depth = 0;
  var currentNode = pmFragment;
  while (currentNode.firstChild) {
    depth++;
    currentNode = currentNode.firstChild;
  }
  return depth;
}
function isSelectionAtParagraphStart($pos) {
  var node = $pos.parent;
  return !!node && (node.type.name === 'paragraph' || node.type.name === 'heading') && !$pos.parentOffset;
}
function isFragmentSingleParagraph(fragment) {
  // Ignored via go/ees005
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return fragment.childCount === 1 && fragment.firstChild.type.name === 'paragraph';
}
function isSelectionInTaskOrDecisionNode($pos) {
  var node = $pos.parent;
  return !!node && (node.type.name === 'decisionList' || node.type.name === 'decisionItem' || node.type.name === 'taskList' || node.type.name === 'taskItem');
}

/**
 * If nested tables experiment is on and the selection is inside a table,
 * we check if the selection is too nested to insert a new table to determine
 * if we should insert after the top table node.
 *
 * We want to limit table nesting to only allowing 1 table to be nested inside another table.
 */
export function shouldInsertAfterTable(_ref2) {
  var selection = _ref2.selection,
    tableSchema = _ref2.tableSchema;
  var isNestedTablesDisabled = editorExperiment('nested-tables-in-tables', false, {
    exposure: true
  });
  var parentTableCount = getParentOfTypeCount(tableSchema)(selection.$from);
  var hasParentTable = hasParentNodeOfType(tableSchema)(selection);
  return hasParentTable && (isNestedTablesDisabled || parentTableCount > 1);
}

/**
 * Insert after the top table node if the selection is too nested.
 */
export var insertContentAfterTable = function insertContentAfterTable(_ref3) {
  var pmFragment = _ref3.pmFragment,
    transaction = _ref3.transaction;
  var tableSchema = transaction.doc.type.schema.nodes.table;
  var fromPos = transaction.selection.$from;
  var positionAfterTopTable = getPositionAfterTopParentNodeOfType(tableSchema)(fromPos);
  return safeInsert(pmFragment, positionAfterTopTable)(transaction);
};
export var replaceWithAIContentForBlock = function replaceWithAIContentForBlock(_ref4) {
  var editorView = _ref4.editorView,
    positions = _ref4.positions,
    pmFragment = _ref4.pmFragment,
    triggeredFor = _ref4.triggeredFor;
  var state = editorView.state;
  var transaction = state.tr;
  var slice = new Slice(pmFragment, 0, 0);
  try {
    transaction.replaceRange(positions[0], positions[1], slice);
  } catch (_unused) {
    // If you can't replace the range, use safeInsert
    safeInsert(pmFragment, undefined, true)(transaction);
    var tr = addAnalytics({
      editorState: state,
      tr: state.tr,
      payload: {
        action: 'fallback',
        actionSubject: 'editorPluginAI',
        actionSubjectId: 'proseMirrorReplace',
        eventType: EVENT_TYPE.OPERATIONAL,
        attributes: {}
      }
    });
    editorView.dispatch(tr);
  }
  beforeDispatchingTransactionForBlocks(pmFragment, transaction, editorView.state);
  beforeDispatchAITransaction(transaction, state);
  return transaction;
};
export var replaceWithAIContent = function replaceWithAIContent(_ref5) {
  var editorView = _ref5.editorView,
    positions = _ref5.positions,
    pmFragment = _ref5.pmFragment,
    _ref5$options = _ref5.options,
    options = _ref5$options === void 0 ? {
      replaceDocOnEmptySelection: true
    } : _ref5$options;
  var state = editorView.state;
  var transaction = state.tr;
  var endPositionDepth = state.doc.resolve(positions[1]).depth;
  var lastFragmentDepth = getLastContentDepth({
    pmFragment: pmFragment
  });
  var sliceOpenEnd = Math.min(endPositionDepth, lastFragmentDepth);
  var slice = new Slice(pmFragment, 1, sliceOpenEnd);
  if (fragmentContainsNodeType(pmFragment, transaction.doc.type.schema.nodes.table) && shouldInsertAfterTable({
    selection: transaction.selection,
    tableSchema: transaction.doc.type.schema.nodes.table
  }) && fg('platform_editor_use_nested_table_pm_nodes')) {
    return insertContentAfterTable({
      pmFragment: pmFragment,
      transaction: transaction
    });
  } else {
    // We check if the positions are the same if so we replace the whole document
    // This is a quick spike so real implementation might need to be different
    // You might need to create a new replace action
    if (options.replaceDocOnEmptySelection && positions[0] === positions[1]) {
      transaction.replaceRange(0, state.doc.content.size, slice);
    } else {
      try {
        transaction.replaceRange(positions[0], positions[1], slice);
        // If you can't replace the range, use safeInsert
      } catch (_unused2) {
        safeInsert(pmFragment, undefined, true)(transaction);
        var tr = addAnalytics({
          editorState: state,
          tr: state.tr,
          payload: {
            action: 'fallback',
            actionSubject: 'editorPluginAI',
            actionSubjectId: 'proseMirrorReplace',
            eventType: EVENT_TYPE.OPERATIONAL,
            attributes: {}
          }
        });
        editorView.dispatch(tr);
      }
    }
    beforeDispatchAITransaction(transaction, state);
    return transaction;
  }
};
export var insertAIContentAtCurrentPosition = function insertAIContentAtCurrentPosition(_ref6) {
  var editorView = _ref6.editorView,
    positions = _ref6.positions,
    pmFragment = _ref6.pmFragment;
  var state = editorView.state;
  var transaction = state.tr;
  var pos = positions[0];
  var $pos = state.doc.resolve(pos);
  var isParagraphStart = isSelectionAtParagraphStart($pos);
  // if selection is at the beginning of paragraph,
  // insert before the cursor so that it inserts without an extra new line
  var insertPos = isParagraphStart ? pos - 1 : pos;

  // if selection is inside decision or task node AND pmFragment is a single paragraph
  // strip the paragraph so it can insert inside the decision/task
  var fragmentToInsert = pmFragment;
  if (isSelectionInTaskOrDecisionNode($pos) && isFragmentSingleParagraph(pmFragment)) {
    // Ignored via go/ees005
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    fragmentToInsert = pmFragment.firstChild.content;
  }
  if (fragmentContainsNodeType(pmFragment, transaction.doc.type.schema.nodes.table) && shouldInsertAfterTable({
    selection: transaction.selection,
    tableSchema: transaction.doc.type.schema.nodes.table
  }) && fg('platform_editor_use_nested_table_pm_nodes')) {
    return insertContentAfterTable({
      pmFragment: pmFragment,
      transaction: state.tr
    });
  } else {
    transaction.insert(insertPos, fragmentToInsert);
    beforeDispatchAITransaction(transaction, state);
    return transaction;
  }
};